import { useIsFetching } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  iso3ToIso2,
  PhoneInput,
} from '@unified-checkout/design-system';
import {
  Plus,
  TrashCan,
} from '@unified-checkout/design-system/icons/index';

import type { IField } from '@app/dashboard/types/outerLayout/common';
import type { TModalAction } from '@app/dashboard/types/innerLayout/profile_details/confirmation-modal';
import TableInfo from '@app/dashboard/components/inner_layout/TableInfo';
import { phoneWithoutCountryCode } from '@app/dashboard/utils/phoneWithoutCountryCode';
import { onFieldChangeHandler } from '@app/dashboard/utils/formValidations';
import {
  MODAL_ACTIONS,
  PHONE_NUMBER_INITIAL_STATE,
  PROFILE_INFO_FIELDS,
} from '@app/dashboard/constants/settings/profile-details';
import { PROFILE } from '@app/dashboard/constants/profile';
import { getProfileType } from '@app/dashboard/utils/localStorageUtils';
import { getCountryFromDomain } from '@app/dashboard/utils/getCountryFromDomain';
import LabelCardInfo from '@app/dashboard/components/inner_layout/LabelCardInfo';

import PasswordConfirmation from '../../modals/PasswordConfirmation';

import styles from './ContactInfo.module.scss';

const ContactInfo = ({ phoneNumbers }: { phoneNumbers: string[] }) => {
  const { t } = useTranslation();
  const isFetching = useIsFetching();
  const country = getCountryFromDomain();

  const [openAddPhoneInput, setOpenAddPhoneInput] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [fields, setFields] = useState<IField>(PHONE_NUMBER_INITIAL_STATE);
  const [modalAction, setModalAction] = useState<TModalAction | null>(null);
  const [targetPhoneNumber, setTargetPhoneNumber] = useState('');

  const isMerchantType = getProfileType() === PROFILE.TYPE.MERCHANT;

  const handleFieldChange = (phone: string) => {
    onFieldChangeHandler(
      fields,
      setFields,
      PROFILE_INFO_FIELDS.PHONE_NUMBER,
      phoneWithoutCountryCode(phone),
      () => {}
    );
  };

  const handleOpenAddPhoneInput = () => {
    setOpenAddPhoneInput(true);
  };

  const handleOpenConfirmationModal = ({
    phoneNumber,
    action,
  }: {
    phoneNumber: string;
    action: TModalAction;
  }) => {
    setOpenModal(true);
    setTargetPhoneNumber(phoneNumber);
    setModalAction(action);
  };

  const handleRemoveField = () => {
    setFields(PHONE_NUMBER_INITIAL_STATE);
    setOpenAddPhoneInput(false);
  };

  const handleAfterConfirmation = () => {
    setOpenAddPhoneInput(false);
    setFields(PHONE_NUMBER_INITIAL_STATE);
  }

  return (
    <LabelCardInfo
      label={t('innerLayout.profileDetailsPage.contactInfo')}
      wrapperClassName={styles['profile-info__contact-info']}
    >
      <PasswordConfirmation
        fields={fields}
        setFields={setFields}
        phoneNumbers={phoneNumbers}
        isModalOpen={openModal}
        setIsModalOpen={setOpenModal}
        modalAction={modalAction as TModalAction}
        targetPhoneNumber={targetPhoneNumber}
        handleAfterConfirmation={handleAfterConfirmation}
      />

      <TableInfo
        maxHeight="20rem"
        columns={[
          {
            id: 'phone',
            name: t('innerLayout.profileDetailsPage.form.phoneNumber'),
            select: (row: string) => row,
            render: (phone: string) => phone,
            width: '90%',
          },
          ...(phoneNumbers.length > 1 && isMerchantType
            ? [
                {
                  id: 'actions',
                  name: '',
                  select: (row: string) => row,
                  render: (phoneNumber: string) => (
                    <Button
                      leadingIcon={TrashCan}
                      variant="link"
                      color="secondary"
                      size="sm"
                      onClickHandler={() => {
                        handleOpenConfirmationModal({
                          phoneNumber,
                          action: MODAL_ACTIONS.DELETE,
                        });
                      }}
                    />
                  ),
                },
              ]
            : []),
        ]}
        data={phoneNumbers}
        componentErrorText={t('innerLayout.profileDetailsPage.noAvailableData')}
        isLoading={!!isFetching}
        skeltonProps={{
          noOfRows: 4,
          height: '12rem',
          type: 'transparent_box',
          width: '100%',
        }}
      />
      {!isMerchantType ? null : !openAddPhoneInput ? (
        <Button
          text={t('innerLayout.profileDetailsPage.actions.newPhoneNumber')}
          size="md"
          variant="link"
          leadingIcon={Plus}
          onClickHandler={handleOpenAddPhoneInput}
          disabled={!!isFetching}
        />
      ) : (
        <div className={styles['profile-info__contact-info-add-phone-wrapper']}>
          <div
            className={styles['profile-info__contact-info-add-phone']}
            data-phone-validation-status={
              fields[PROFILE_INFO_FIELDS.PHONE_NUMBER].errorMsg
                ? 'error'
                : 'default'
            }
          >
            <PhoneInput
              id={PROFILE_INFO_FIELDS.PHONE_NUMBER}
              name={PROFILE_INFO_FIELDS.PHONE_NUMBER}
              label={t('outerLayout.form.labels.phoneNumber')}
              disableSelectCountry
              autoFocus
              onChangeHandler={handleFieldChange}
              country={iso3ToIso2(country)}
              error={fields[PROFILE_INFO_FIELDS.PHONE_NUMBER].errorMsg}
              placeholder={t(
                'outerLayout.form.register.placeholders.enterUrPhoneNum'
              )}
              autoComplete={false}
            />

            <Button
              text={t('innerLayout.profileDetailsPage.actions.add')}
              size="md"
              variant="contained"
              onClickHandler={() =>
                handleOpenConfirmationModal({
                  phoneNumber: fields[PROFILE_INFO_FIELDS.PHONE_NUMBER]
                    .value as string,
                  action: MODAL_ACTIONS.ADD,
                })
              }
              disabled={!fields[PROFILE_INFO_FIELDS.PHONE_NUMBER].isValid}
            />
          </div>
          <Button
            text={t('form.removeField')}
            size="md"
            variant="link"
            color="secondary"
            leadingIcon={TrashCan}
            onClickHandler={handleRemoveField}
          />
        </div>
      )}
    </LabelCardInfo>
  );
};

export default ContactInfo;
